import styled from '@emotion/styled';
import Link from 'next/link';
import { FC } from 'react';

import { FontFamily } from '@/enums/index';
import { NewsArticle } from '@/resources/NewsArticle';
import { BREAKPOINTS } from '@/styles/themes/default';
import { colors } from '@/styles/themes/foundations/colors';
import { IThemeProps } from '@/styles/themes/types';
import { lineClampStyles } from '@/styles/utils';

import { ClampedMarkdown } from '../ClampedMarkdown';

const ArticleThumbnailContainer = styled('article')<IThemeProps>`
  width: 100%;
  color: black;
  cursor: pointer;
  position: relative;

  &:hover h3 {
    font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.SpaceGroteskUnderline]};
  }

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    height: 550px;
    width: 368px;
  }
`;

const ArticleLink = styled(Link)<IThemeProps>`
  text-decoration: none;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) and (max-width: ${BREAKPOINTS.Tablet.max}px) {
    display: flex;
  }
`;

const ArticleThumbnailInfoContainer = styled('div')<IThemeProps>`
  padding: 24px 0 0 0;
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.IBMPlexSans]};

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    padding: 32px 0 0 0;
  }

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) and (max-width: ${BREAKPOINTS.Tablet.max}px) {
    flex: 1;
    padding: 0 32px;
  }
`;

const ArticleThumbnailHeader = styled('p')<IThemeProps>`
  color: ${({ theme }) => theme.legacy.colors.lightText};
  margin: 0 0 8px 0;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.legacy.colors.lightText};
  font-family: ${FontFamily.IBMPlexSans};
  font-weight: normal;
`;

const ArticleAuthor = styled.span<IThemeProps>`
  color: ${({ theme }) => theme.legacy.colors.brand.primary};
  font-family: ${FontFamily.SpaceGrotesk};
  font-weight: bold;
  margin-right: 16px;
`;

const ArticleTitle = styled.h3<IThemeProps>`
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.SpaceGrotesk]};
  color: black;
  font-size: 24px;
  line-height: 32px;

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    font-size: 32px;
    line-height: 40px;
  }
`;

const Description = styled.div<IThemeProps>`
  margin: 24px 0 0;
  font-size: 16px;
  line-height: 24px;
  font-family: ${({ theme }) => theme.legacy.typography.fontFamily[FontFamily.IBMPlexSerif]};
  min-height: 100px;
  color: ${({ theme }) => theme.legacy.colors.lightText};

  @media (min-width: ${BREAKPOINTS.Desktop.min}px) {
    font-size: 20px;
    line-height: 32px;
  }
`;

const ArticleThumbnailCover = styled.img<IThemeProps>`
  width: 100%;
  /* gray.50 (#F3F5F5) shows through transparent artwork */
  background-color: ${colors.gray[50]};
  height: 219px;
  min-height: 219px;
  object-fit: cover;
  object-position: center;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) and (max-width: ${BREAKPOINTS.Tablet.max}px) {
    flex: 1;
    max-width: 304px;
  }
`;

const StyledMarkdown = styled(ClampedMarkdown)`
  ${lineClampStyles(4)}
  & > * {
    display: none;
  }

  /* we are using ClampedMarkdown component which adds class to first node */
  /* because first-child can't be used, and first-of-type is not the solution */
  & > .first-markdown-element {
    margin: 0;
    display: initial;
  }
`;

interface IArticleThumbnailProps {
  article: NewsArticle;
}

export const ArticleThumbnail: FC<IArticleThumbnailProps> = ({
  article: { title, summary, author, linkSlug, readTime, mediumFeaturedImageUrl },
  ...restProps
}) => {
  return (
    <ArticleThumbnailContainer {...restProps}>
      <ArticleLink href={`/news/${linkSlug}`} target="_blank" rel="noopener noreferrer">
        <ArticleThumbnailCover src={mediumFeaturedImageUrl} alt="article cover" />
        <ArticleThumbnailInfoContainer>
          <ArticleThumbnailHeader>
            <ArticleAuthor>{author}</ArticleAuthor>&nbsp;
            {readTime && `${readTime} min read`}
          </ArticleThumbnailHeader>
          <ArticleTitle css={lineClampStyles(3)}>{title}</ArticleTitle>
          {summary && (
            <Description>
              <StyledMarkdown source={summary} />
            </Description>
          )}
        </ArticleThumbnailInfoContainer>
      </ArticleLink>
    </ArticleThumbnailContainer>
  );
};
